/**
 * Free-tier local storage (IndexedDB)
 *
 * Draw.io-inspired "no backend" mode. Folders and projects live entirely
 * in the user's browser. Everything is keyed to a single synthetic user
 * ("local-user") — no auth, no server.
 *
 * The public API mirrors `api.service.ts` so the dispatcher can swap
 * between remote and local with no component-level changes.
 */

import type { Project, Folder, CreateProjectInput } from '@inno-ck/shared';

export const LOCAL_USER_ID = 'local-user';

const DB_NAME = 'inno-ck-local';
const DB_VERSION = 1;
const FOLDER_STORE = 'folders';
const PROJECT_STORE = 'projects';

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FOLDER_STORE)) {
        db.createObjectStore(FOLDER_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PROJECT_STORE)) {
        db.createObjectStore(PROJECT_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result: T;
    Promise.resolve(run(store)).then(v => { result = v; });
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const genId = (prefix: string): string =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export const localFolderService = {
  async list(userId: string): Promise<Folder[]> {
    return tx(FOLDER_STORE, 'readonly', async (store) => {
      const all = await reqToPromise(store.getAll() as IDBRequest<Folder[]>);
      return all.filter(f => f.userId === userId);
    });
  },

  async create(userId: string, name: string, parentId: string | null = null): Promise<Folder> {
    const folder: Folder = {
      id: genId('localfolder'),
      userId,
      name,
      parentId,
      createdAt: new Date().toISOString(),
    } as Folder;
    await tx(FOLDER_STORE, 'readwrite', (store) => reqToPromise(store.put(folder)));
    return folder;
  },

  async update(folderId: string, name: string): Promise<Folder> {
    return tx(FOLDER_STORE, 'readwrite', async (store) => {
      const existing = await reqToPromise(store.get(folderId) as IDBRequest<Folder | undefined>);
      if (!existing) throw new Error('Folder not found');
      const updated = { ...existing, name };
      await reqToPromise(store.put(updated));
      return updated;
    });
  },

  async remove(folderId: string): Promise<void> {
    await tx(FOLDER_STORE, 'readwrite', (store) => reqToPromise(store.delete(folderId)));
    // Move projects from deleted folder to root
    await tx(PROJECT_STORE, 'readwrite', async (store) => {
      const all = await reqToPromise(store.getAll() as IDBRequest<Project[]>);
      for (const p of all) {
        if (p.folderId === folderId) {
          await reqToPromise(store.put({ ...p, folderId: null }));
        }
      }
    });
  },
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export const localProjectService = {
  async list(userId: string): Promise<Project[]> {
    return tx(PROJECT_STORE, 'readonly', async (store) => {
      const all = await reqToPromise(store.getAll() as IDBRequest<Project[]>);
      return all.filter(p => p.userId === userId);
    });
  },

  async get(projectId: string): Promise<Project> {
    return tx(PROJECT_STORE, 'readonly', async (store) => {
      const p = await reqToPromise(store.get(projectId) as IDBRequest<Project | undefined>);
      if (!p) throw new Error('Project not found');
      return p;
    });
  },

  async upsertByName(input: CreateProjectInput): Promise<Project> {
    return tx(PROJECT_STORE, 'readwrite', async (store) => {
      const all = await reqToPromise(store.getAll() as IDBRequest<Project[]>);
      const existing = all.find(p => p.userId === input.userId && p.projectName === input.projectName);
      if (existing) {
        const updated: Project = {
          ...existing,
          jsonData: input.jsonData,
          folderId: input.folderId ?? null,
        };
        await reqToPromise(store.put(updated));
        return updated;
      }
      const created: Project = {
        id: genId('localproject'),
        userId: input.userId,
        projectName: input.projectName,
        jsonData: input.jsonData,
        folderId: input.folderId ?? null,
        parentId: input.parentId ?? null,
      };
      await reqToPromise(store.put(created));
      return created;
    });
  },

  async patch(projectId: string, patch: Partial<Project>): Promise<Project> {
    return tx(PROJECT_STORE, 'readwrite', async (store) => {
      const existing = await reqToPromise(store.get(projectId) as IDBRequest<Project | undefined>);
      if (!existing) throw new Error('Project not found');
      const updated: Project = { ...existing, ...patch } as Project;
      await reqToPromise(store.put(updated));
      return updated;
    });
  },

  async deleteByName(userId: string, projectName: string): Promise<void> {
    await tx(PROJECT_STORE, 'readwrite', async (store) => {
      const all = await reqToPromise(store.getAll() as IDBRequest<Project[]>);
      const match = all.find(p => p.userId === userId && p.projectName === projectName);
      if (match) await reqToPromise(store.delete(match.id));
    });
  },
};

// ---------------------------------------------------------------------------
// Export / Import — draw.io-style portable snapshot
// ---------------------------------------------------------------------------

export interface LocalSnapshot {
  version: 1;
  exportedAt: string;
  folders: Folder[];
  projects: Project[];
}

export async function exportSnapshot(userId: string): Promise<LocalSnapshot> {
  const folders = await localFolderService.list(userId);
  const projects = await localProjectService.list(userId);
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    folders,
    projects,
  };
}

export async function importSnapshot(snapshot: LocalSnapshot, targetUserId: string): Promise<void> {
  if (snapshot.version !== 1) throw new Error('Unsupported snapshot version');
  await tx(FOLDER_STORE, 'readwrite', async (store) => {
    for (const f of snapshot.folders) {
      await reqToPromise(store.put({ ...f, userId: targetUserId }));
    }
  });
  await tx(PROJECT_STORE, 'readwrite', async (store) => {
    for (const p of snapshot.projects) {
      await reqToPromise(store.put({ ...p, userId: targetUserId }));
    }
  });
}
